import { useQuery } from '@tanstack/react-query';
import { isNil } from 'lodash';
import {
  FIRM_SUMMARY_DEFINITION_CHART_DATA_DEFAULT_ERROR_MESSAGE,
  FIRM_SUMMARY_DEFINITION_CHART_DATA_QUERY_KEY,
  FIRM_SUMMARY_DEFINITION_CHART_DATA_STALE_TIME,
  SERVICES_FIRMS_REFETCH_ON_WINDOW_FOCUS,
} from 'common/constants/services/firms';
import { ChartDataResponse } from 'services/summaryDefinitions';
import SummaryDefinitionsService from 'services/summaryDefinitions';
import { throwError } from 'utilities';
import { GetFirmChartDataReturn, UseGetFirmChartDataParams } from './types';

const service = new SummaryDefinitionsService();

const getFirmChartData = async (
  firmId: number,
  definitionId: number
): Promise<ChartDataResponse | null> => {
  try {
    const result = await service.getFirmDefinitionChartData(firmId, definitionId);

    if (!result) {
      return throwError(FIRM_SUMMARY_DEFINITION_CHART_DATA_DEFAULT_ERROR_MESSAGE);
    }

    return result;
  } catch (error) {
    return throwError(FIRM_SUMMARY_DEFINITION_CHART_DATA_DEFAULT_ERROR_MESSAGE, error);
  }
};

const useGetFirmChartData = (params: UseGetFirmChartDataParams): GetFirmChartDataReturn => {
  const { firmId, definitionId, shouldQueryAutomatically = true } = params;

  const { data, error, isError, isFetched, isFetching, isLoading, refetch } = useQuery<
    GetFirmChartDataReturn['data'],
    Error
  >({
    enabled: !isNil(firmId) && !isNil(definitionId) && shouldQueryAutomatically,
    queryFn: () => getFirmChartData(firmId!, definitionId!),
    queryKey: [FIRM_SUMMARY_DEFINITION_CHART_DATA_QUERY_KEY, firmId, definitionId],
    refetchOnWindowFocus: SERVICES_FIRMS_REFETCH_ON_WINDOW_FOCUS,
    staleTime: FIRM_SUMMARY_DEFINITION_CHART_DATA_STALE_TIME,
  });

  return {
    data,
    error,
    isError,
    isFetched,
    isFetching,
    isLoading,
    refetch,
  };
};

export default useGetFirmChartData;
